/**
 * Recovery from a page file that no longer exists after a deploy.
 *
 * Every page is a lazily loaded chunk with a content hash in its name, and a
 * deploy renames most of them. A tab opened before the deploy still asks for
 * the old names; Vercel answers those with index.html, the import fails and
 * the app-wide error boundary shows the 500 page. Only a real reload fetches
 * the new index.html and its new chunk names, so we reload once.
 *
 * ONCE, GUARDED BY sessionStorage: if the file is really missing (not just
 * renamed), reloading again would loop forever. Without sessionStorage there
 * is no guard, so we do not reload at all and the error page shows instead.
 */

const KEY = "prism:stale-chunk-reload-at"
const WINDOW_MS = 30_000

/** True for the browsers' "a lazily imported module failed to load" errors. */
export function isStaleChunkError(error: unknown): boolean {
  if (!(error instanceof Error)) return false
  const msg = (error.message ?? "").toLowerCase()
  if (!msg) return false
  // Chrome: "Failed to fetch dynamically imported module: …"
  // Firefox: "error loading dynamically imported module: …"
  if (msg.includes("dynamically imported module")) return true
  // Safari: "Importing a module script failed."
  if (msg.includes("importing a module script failed")) return true
  // A chunk answered with index.html: "… module script … MIME type of text/html …"
  return msg.includes("module script") && msg.includes("mime type")
}

/**
 * Reloads the page unless it already did so in the last 30 seconds. Returns
 * true when it reloaded. Never reloads when sessionStorage is unavailable.
 */
export function reloadOnceForStaleChunk(): boolean {
  let storage: Storage
  try {
    storage = window.sessionStorage
    if (!storage) return false
  } catch {
    return false
  }
  const now = Date.now()
  let last: number
  try {
    last = Number(storage.getItem(KEY) ?? 0)
  } catch {
    return false
  }
  if (Number.isFinite(last) && last > 0 && now - last < WINDOW_MS) return false
  try {
    storage.setItem(KEY, String(now))
  } catch {
    return false
  }
  window.location.reload()
  return true
}

/**
 * Vite fires `vite:preloadError` when it cannot preload a lazy chunk's
 * dependencies. preventDefault stops Vite rethrowing it; the reload fetches the
 * new chunk names. Called once from main.tsx.
 */
export function installStaleChunkReload(target: Window = window): void {
  target.addEventListener("vite:preloadError", (event) => {
    event.preventDefault()
    reloadOnceForStaleChunk()
  })
}
